import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { api } from '@/lib/api'
import { AimeLogo } from '@/components/AimeLogo'

export default function ResetPasswordPage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [success, setSuccess] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  if (!token) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center p-6">
        <div className="flex w-full max-w-sm flex-col items-center space-y-6 text-center">
          <AimeLogo size={120} />
          <h1 className="sr-only">Aime — {t('auth.resetInvalidToken')}</h1>
          <p className="text-destructive text-sm">{t('auth.resetInvalidToken')}</p>
          <Link
            to="/sign-in"
            className="bg-primary text-primary-foreground hover:bg-primary/90 inline-flex h-10 w-full items-center justify-center rounded-xl px-4 py-2 text-sm font-medium"
          >
            {t('auth.backToSignIn')}
          </Link>
        </div>
      </div>
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (password !== confirmPassword) {
      setError(t('auth.confirmPassword'))
      return
    }
    setLoading(true)
    try {
      await api('/api/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, newPassword: password }),
      })
      setSuccess(true)
    } catch {
      setError(t('auth.resetInvalidToken'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center p-6">
      <div className="w-full max-w-sm space-y-6">
        <div className="flex flex-col items-center gap-3 text-center">
          <AimeLogo size={120} />
          <h1 className="sr-only">Aime — {t('auth.resetPasswordTitle')}</h1>
          <p className="text-on-surface-variant mt-1">{t('auth.resetPasswordTitle')}</p>
        </div>

        {success ? (
          <div className="space-y-4">
            <p className="text-on-surface-variant text-center text-sm">{t('auth.resetSuccess')}</p>
            <Link
              to="/sign-in"
              className="bg-primary text-primary-foreground hover:bg-primary/90 inline-flex h-10 w-full items-center justify-center rounded-xl px-4 py-2 text-sm font-medium"
            >
              {t('auth.signIn')}
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium" htmlFor="password">
                {t('auth.newPassword')}
              </label>
              <input
                id="password"
                type="password"
                required
                autoComplete="new-password"
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="border-outline-variant/30 bg-surface-container ring-offset-background focus-visible:ring-ring mt-1 flex h-10 w-full rounded-xl border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:outline-none"
              />
            </div>
            <div>
              <label className="text-sm font-medium" htmlFor="confirmPassword">
                {t('auth.confirmPassword')}
              </label>
              <input
                id="confirmPassword"
                type="password"
                required
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="border-outline-variant/30 bg-surface-container ring-offset-background focus-visible:ring-ring mt-1 flex h-10 w-full rounded-xl border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:outline-none"
              />
            </div>

            {error && <p className="text-destructive text-sm">{error}</p>}

            <button
              type="submit"
              disabled={loading}
              className="bg-primary text-primary-foreground hover:bg-primary/90 inline-flex h-10 w-full items-center justify-center rounded-xl px-4 py-2 text-sm font-medium disabled:opacity-50"
            >
              {loading ? '...' : t('auth.resetPassword')}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
